import { type DeliveryAddress, type Location } from "@koala/sdk";
import Link from "next/link";
import { useEffect, useRef } from "react";
import SwitchToDeliveryConfirm from "./deliveryConfirm";
import { DeliveryForm } from "./deliveryForm";
import { StyledFormCTA, StyledFormContainer } from "./styles";
import { genericEventHandler } from "@/analytics/events";
import { EventNames, GlobalEvents } from "@/analytics/events/constants";
import StringAccessor from "@/components/cmsConfig/stringAccessor";
import { MODAL } from "@/constants/events";
import { ROUTES } from "@/constants/routes";
import { type IBasketFulfillment } from "@/types/fulfillment";
import { type IGlobalFulfillmentModal } from "@/types/global";

interface Props {
  toggleFulfillmentModal: (
    show: boolean,
    location?: Location,
    fulfillmentType?: string
  ) => void;
  checkDeliveryAddress: (
    address: DeliveryAddress,
    location: Location,
    revalidate?: boolean,
    shouldAutoRedirect?: boolean
  ) => void;
  deliveryAddressValidating: boolean;
  fulfillmentModal: IGlobalFulfillmentModal;
  basketFulfillment: IBasketFulfillment;
  revalidate: boolean;
}

export const DeliveryModalStateContainer = ({
  fulfillmentModal,
  toggleFulfillmentModal,
  checkDeliveryAddress,
  basketFulfillment,
  deliveryAddressValidating,
  revalidate,
}: Props) => {
  const address = basketFulfillment?.address;
  const containerRef = useRef<HTMLDivElement>(null);

  // Move focus to an appropriate element when the modal opens
  useEffect(() => {
    if (containerRef.current) {
      // Use requestAnimationFrame to ensure the modal is fully rendered
      requestAnimationFrame(() => {
        if (containerRef.current) {
          // Try to focus the first input field (most appropriate for a form)
          const firstInput = containerRef.current.querySelector(
            "input, select, textarea"
          )!;
          if (firstInput) {
            firstInput.focus();
            return;
          }

          // If no input, try to focus the first button
          const firstButton = containerRef.current.querySelector(
            "button, a[href]"
          )!;
          if (firstButton) {
            firstButton.focus();
            return;
          }

          // Fallback: focus the container itself
          containerRef.current.focus();
        }
      });
    }
  }, []);

  if (fulfillmentModal.confirm) {
    return (
      <SwitchToDeliveryConfirm
        toggleFulfillmentModal={toggleFulfillmentModal}
        /** @TODO ensure that `location` isn't null. */
        // @ts-expect-error
        fulfillmentModalLocation={fulfillmentModal.location}
      />
    );
  }

  return (
    <StyledFormContainer ref={containerRef} tabIndex={-1}>
      <DeliveryForm
        checkDeliveryAddress={(
          values: DeliveryAddress,
          shouldAutoRedirect?: boolean
        ) =>
          checkDeliveryAddress(
            values,
            /** @TODO ensure that `location` isn't null. */
            // @ts-expect-error
            fulfillmentModal.location,
            revalidate,
            shouldAutoRedirect
          )
        }
        initialValues={address}
        loading={deliveryAddressValidating}
      />

      {fulfillmentModal.disableModalClose && (
        <StyledFormCTA>
          <Link href={ROUTES.LOCATIONS} passHref={true}>
            <a
              onClick={() => {
                toggleFulfillmentModal(false);
                genericEventHandler(GlobalEvents.GENERIC__CTA, {
                  name: EventNames.CHANGE_LOCATION,
                  details: MODAL.FULFILLMENT_MANAGER,
                });
              }}
            >
              <StringAccessor accessor="cart_checkout.change_location_cta" />
            </a>
          </Link>
        </StyledFormCTA>
      )}
    </StyledFormContainer>
  );
};
